import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Archive, X, RefreshCw, Search, Plus, Users, Share2, Baby, Check, UserMinus, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { apiFetch, apiPatch, apiPost } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// Perfil de chegada na fe/igreja (ver schema.prisma Member.faithOriginProfile)
// -- pedido do Rodrigo, 24/10/2026: saber quantos decidiram seguir Jesus
// agora, quantos vieram de outra igreja, quantos estavam sem igreja.
const FAITH_ORIGIN_OPTIONS = ["DECIDIU_AGORA", "OUTRA_IGREJA", "SEM_IGREJA", "OUTRO"];
const FAITH_ORIGIN_LABELS = {
  DECIDIU_AGORA: "Decidiu seguir Jesus agora",
  OUTRA_IGREJA: "Veio de outra igreja",
  SEM_IGREJA: "Estava um tempo sem igreja",
  OUTRO: "Outro",
  NAO_INFORMADO: "Não informado"
};

// Sexo/genero (ver schema.prisma Member.gender) -- sem coleta nenhuma antes
// disso, adicionado junto do dashboard de membresia pedido pelo Rodrigo,
// 24/10/2026.
const GENDER_OPTIONS = ["MASCULINO", "FEMININO", "OUTRO"];
const GENDER_LABELS = {
  MASCULINO: "Masculino",
  FEMININO: "Feminino",
  OUTRO: "Outro",
  NAO_INFORMADO: "Não informado"
};

// Faixas de idade do dashboard -- calculadas a partir de Member.birthDate
// (preenchido no cadastro, cobre toda a base, nao so quem tem conta no
// app). Pedido do Rodrigo, 24/10/2026.
const AGE_BRACKETS = [
  { key: "0-17", label: "Até 17 anos", min: 0, max: 17 },
  { key: "18-25", label: "18 a 25 anos", min: 18, max: 25 },
  { key: "26-35", label: "26 a 35 anos", min: 26, max: 35 },
  { key: "36-45", label: "36 a 45 anos", min: 36, max: 45 },
  { key: "46-60", label: "46 a 60 anos", min: 46, max: 60 },
  { key: "61+", label: "61 anos ou mais", min: 61, max: Infinity }
];

function ageFromBirthDate(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age;
}

// Nome amigavel de cada campo diffavel de Member (mesma lista do backend,
// MEMBER_AUDIT_FIELDS em routes/members.js) -- usado so' pra exibir o
// historico de edicoes (GET /members/:id/audit-log) de um jeito legivel,
// em vez do nome bruto do campo no banco.
const MEMBER_FIELD_LABELS = {
  fullName: "Nome completo",
  nickname: "Apelido (como chamam)",
  email: "E-mail",
  birthDate: "Data de nascimento",
  cpf: "CPF",
  address: "Endereço",
  addressComplement: "Complemento",
  state: "Estado",
  city: "Cidade",
  neighborhood: "Bairro",
  zipCode: "CEP",
  mobilePhone: "Celular",
  homePhone: "Telefone fixo",
  emergencyPhone: "Telefone de emergência",
  emergencyContactName: "Nome do contato de emergência",
  spouseName: "Nome do cônjuge",
  fatherName: "Nome do pai",
  motherName: "Nome da mãe",
  specialNeeds: "Necessidade especial",
  baptismDate: "Data de batismo",
  notBaptized: "Não é batizado",
  baptismChurch: "Igreja onde se batizou",
  churchRole: "Função que exerce ou exercia",
  howMetChurch: "Como conheceu a IBM",
  faithOriginProfile: "Chegada na fé/igreja",
  gender: "Sexo",
  attendingSince: "Começou a frequentar em",
  status: "Situação",
  notes: "Observações da secretaria",
  householdId: "Família (householdId)",
  linkedUserId: "Conta do app vinculada"
};

const STATUS_OPTIONS = ["ATIVO", "INATIVO", "TRANSFERIDO", "FALECIDO", "PENDENTE"];
const STATUS_LABELS = {
  ATIVO: "Ativo",
  INATIVO: "Inativo",
  TRANSFERIDO: "Transferido",
  FALECIDO: "Falecido",
  // Veio do autocadastro publico (/cadastro/membro) e ainda ninguem da
  // secretaria revisou -- trocar pra "Ativo" aqui no editar e a aprovacao.
  PENDENTE: "Pendente"
};
const STATUS_COLORS = {
  ATIVO: { border: "rgba(74,222,128,0.45)", background: "rgba(34,197,94,0.14)", color: "#86EFAC" },
  INATIVO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.12)", color: "#c7ced9" },
  TRANSFERIDO: { border: "rgba(125,211,252,0.45)", background: "rgba(14,165,233,0.14)", color: "#7DD3FC" },
  FALECIDO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.08)", color: "#9fb0cc" },
  PENDENTE: { border: "rgba(251,191,36,0.45)", background: "rgba(251,191,36,0.14)", color: "#FDE68A" }
};

const EMPTY_FORM = {
  fullName: "",
  nickname: "",
  email: "",
  birthDate: "",
  cpf: "",
  address: "",
  addressComplement: "",
  state: "",
  city: "",
  neighborhood: "",
  zipCode: "",
  mobilePhone: "",
  homePhone: "",
  emergencyPhone: "",
  emergencyContactName: "",
  spouseName: "",
  fatherName: "",
  motherName: "",
  specialNeeds: "",
  baptismDate: "",
  notBaptized: false,
  baptismChurch: "",
  churchRole: "",
  howMetChurch: "",
  faithOriginProfile: "",
  gender: "",
  attendingSince: "",
  status: "ATIVO",
  notes: "",
  linkedUserId: ""
};

function dateForInput(value) {
  if (!value) return "";
  const iso = String(value);
  return iso.length >= 10 ? iso.slice(0, 10) : "";
}

// Le mes/dia direto da string ISO (ex.: "1990-04-17...") em vez de montar um
// Date e chamar getMonth() -- assim o fuso horario do navegador nao pode
// empurrar a data pro dia anterior perto da meia-noite.
// So pra exibicao -- normaliza a capitalizacao da cidade (que vem digitada de
// jeitos diferentes no cadastro: "CURITIBA", "osasco", "São Paulo") sem tocar
// no dado salvo. Mantem acentos, so ajusta maiuscula/minuscula por palavra.
function titleCaseCity(value) {
  if (!value) return value;
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

function formToBody(form) {
  return {
    ...form,
    email: form.email.trim() || null,
    birthDate: form.birthDate || null,
    baptismDate: form.notBaptized ? null : (form.baptismDate || null),
    attendingSince: form.attendingSince || null,
    linkedUserId: form.linkedUserId || null
  };
}

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

// Mesma convencao de cor/rotulo do historico de visitante (ver
// interactionKindStyle em AdminVisitantes.jsx) -- WHATSAPP e o que o Barna
// grava sozinho (ver agent/ficha.js), NOTE e anotacao manual da equipe.
const memberInteractionKindStyle = {
  WHATSAPP: { color: "#86EFAC", label: "WhatsApp" },
  NOTE: { color: "#CBD5E1", label: "Anotação" }
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 680,
  margin: "3vh auto",
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit",
  // Sem isso o calendario nativo do <input type="date"> abre no tema claro
  // do sistema, destoando do resto do formulario (que e escuro).
  colorScheme: "dark"
};

const textareaStyle = { ...inputStyle, resize: "vertical", minHeight: 64, lineHeight: 1.5 };

const selectStyle = { ...inputStyle };

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

const sectionTitleStyle = {
  margin: "22px 0 10px",
  fontSize: 11,
  letterSpacing: 1.1,
  textTransform: "uppercase",
  fontWeight: 900,
  color: "#7DD3FC"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

// Formata um valor de MemberAuditLog (oldValue/newValue, sempre string ou
// null, ver auditFieldToString no backend) pra exibicao -- traduz enum
// (status/gender/faithOriginProfile) pelos labels que a tela ja usa,
// formata data em pt-BR, "true"/"false" em Sim/Não, e null em "(vazio)".
function formatAuditValue(field, value) {
  if (value === null || value === undefined || value === "") return "(vazio)";
  if (field === "status") return STATUS_LABELS[value] || value;
  if (field === "gender") return GENDER_LABELS[value] || value;
  if (field === "faithOriginProfile") return FAITH_ORIGIN_LABELS[value] || value;
  if (field === "notBaptized") return value === "true" ? "Sim" : "Não";
  if (["birthDate", "baptismDate", "attendingSince"].includes(field)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString("pt-BR");
  }
  return value;
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

// `repeat(N, 1fr)` (o que tinha antes) mantem N colunas fixas mesmo quando a
// tela e estreita -- no modal de editar membro, no celular, isso espremia
// cada campo a uns 130px, e o <input type="date"> nesse tamanho corta a data
// por extenso ("13 de set. de 1985"). `auto-fit` com minmax troca a
// contagem de colunas pela LARGURA disponivel: com espaco (desktop/tablet)
// rende igual a antes (colunas vazias de sobra colapsam a 0px, e o "fit"
// e o que evita isso, diferente de "auto-fill"); sem espaco (celular),
// empilha os campos em vez de espremer.
function FieldRow({ children, columns = 2 }) {
  const minColumnWidth = columns >= 3 ? 130 : 180;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
        gap: 12,
        marginBottom: 12
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, required, ...rest }) {
  return (
    <div>
      <label style={fieldLabelStyle}>
        {label}
        {required && <span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span>}
      </label>
      <input style={inputStyle} {...rest} />
    </div>
  );
}

// Card de KPI do dashboard de membresia (aba "Dashboard") -- mesmo visual
// que ja existia pro card de "Chegada na fe/igreja", so' que reaproveitado
// em varios cards agora. Pedido do Rodrigo, 24/10/2026.
const dashboardCardTitleStyle = {
  margin: "0 0 10px",
  fontSize: 11,
  letterSpacing: 1.1,
  textTransform: "uppercase",
  fontWeight: 900,
  color: "#7DD3FC"
};

// Barra horizontal (tipo "leaderboard") pra cada linha de um card do
// dashboard -- mais visual que so' o numero solto, sem precisar de
// biblioteca de grafico nenhuma (nao tem nenhuma instalada no projeto).
// Uma unica cor (accent azul do proprio admin) porque a identidade de cada
// linha ja vem do rotulo, nao da cor -- nao e' grafico categorico.
function BarListRow({ label, value, percent, maxValue, onClick, expanded }) {
  const widthPct = maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 3 : 0) : 0;
  const clickable = typeof onClick === "function" && value > 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        onClick={clickable ? onClick : undefined}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 10,
          marginBottom: 4,
          cursor: clickable ? "pointer" : "default",
          borderRadius: 6,
          padding: clickable ? "2px 4px" : 0,
          margin: clickable ? "-2px -4px 4px" : "0 0 4px"
        }}
      >
        <span style={{ fontSize: 12.5, color: "#c7d1e4", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
          {label}
          {clickable && (expanded ? <ChevronUp size={12} style={{ color: "#7DD3FC" }} /> : <ChevronDown size={12} style={{ color: "#6b7d99" }} />)}
        </span>
        <span style={{ fontSize: 12.5, color: "#E5E7EB", fontWeight: 900, whiteSpace: "nowrap" }}>
          {value}
          {percent !== null && <span style={{ color: "#6b7d99", fontWeight: 700, marginLeft: 4 }}>({percent}%)</span>}
        </span>
      </div>
      <div style={{ height: 10, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${widthPct}%`,
            borderRadius: 999,
            background: "linear-gradient(90deg, #0EA5E9, #7DD3FC)",
            transition: "width 0.3s ease"
          }}
        />
      </div>
    </div>
  );
}

function BarListCard({ title, rows, total, onRowClick, expandedRowKey, renderExpanded }) {
  const maxValue = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div style={cardStyle}>
      <p style={dashboardCardTitleStyle}>{title}</p>
      {rows.map((row) => (
        <div key={row.key}>
          <BarListRow
            label={row.label}
            value={row.value}
            percent={total > 0 ? Math.round((row.value / total) * 100) : null}
            maxValue={maxValue}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            expanded={expandedRowKey === row.key}
          />
          {expandedRowKey === row.key && renderExpanded && renderExpanded(row)}
        </div>
      ))}
    </div>
  );
}

export default function AdminMembros({ portalAccess } = {}) {
  const [status, setStatus] = useState("checking");
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ativos"); // "ativos" | "pendentes" | "inativos"
  const [view, setView] = useState("lista"); // "lista" | "dashboard" -- dashboard de KPIs pedido pelo Rodrigo, 24/10/2026
  const [genderDrilldownOpen, setGenderDrilldownOpen] = useState(false); // drill-down do card "Sexo" > "Nao informado", 28/10/2026
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [linkableUsers, setLinkableUsers] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null); // null = criando novo
  const [form, setForm] = useState(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const formErrorRef = useRef(null);

  // Historico (aba "Registrar anotacao" dentro do modal de editar membro) --
  // so existe pra quem ja tem id (nao aparece no form de "Novo membro").
  const [memberNoteForm, setMemberNoteForm] = useState({ title: "", note: "" });
  const [memberNoteSaving, setMemberNoteSaving] = useState(false);
  // Historico de edicoes do cadastro (MemberAuditLog) -- carregado so ao
  // abrir o editar de um membro existente (ver openEditForm), nao vem
  // junto da lista/memberSelect de proposito (evitaria trazer o historico
  // inteiro de todo mundo so pra montar a tabela). Pedido da Carla,
  // 24/10/2026: "na auditoria nao da pra saber quem editou este cadastro?".
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  // O aviso de validacao/erro fica no topo do modal; se o usuario estiver
  // rolado mais pra baixo (por exemplo mexendo em "Status e vinculo com o
  // app"), o aviso ficava fora da tela e parecia que o Salvar nao fazia
  // nada. Rolar ate ele quando aparecer resolve isso.
  useEffect(() => {
    if (formError && formErrorRef.current) {
      formErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [formError]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const [householdSuggestions, setHouseholdSuggestions] = useState([]);
  const [householdChecked, setHouseholdChecked] = useState({}); // groupIndex -> Set("member:id" | "child:id")
  // Sugestoes de "essa conta do app parece ser desse membro" -- mesmo
  // racional das sugestoes de familia acima, so que cruzando Member x User
  // por e-mail ou nome (ver /members/account-link-suggestions).
  const [accountLinkSuggestions, setAccountLinkSuggestions] = useState([]);
  const [dismissedAccountSuggestions, setDismissedAccountSuggestions] = useState(() => new Set());
  const [linkingMemberId, setLinkingMemberId] = useState(null);
  const [musicoSaving, setMusicoSaving] = useState(false);
  const [ministeriosSaving, setMinisteriosSaving] = useState(false);
  // Criancas/adolescentes ativas -- so pra casar familia com membros nesta
  // tela (as sugestoes e o modal "ver familia" agora podem incluir
  // criancas). Editar uma crianca continua sendo la em /admin/criancas.
  const [childrenList, setChildrenList] = useState([]);
  const [confirmingGroup, setConfirmingGroup] = useState(null);
  const [dismissedGroups, setDismissedGroups] = useState(() => new Set());
  const [familyViewHouseholdId, setFamilyViewHouseholdId] = useState(null);
  // Busca (independente da busca principal da lista) pra adicionar mais
  // alguem a familia aberta no modal "ver familia".
  const [addMemberQuery, setAddMemberQuery] = useState("");
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberSearching, setAddMemberSearching] = useState(false);
  const [addingMemberId, setAddingMemberId] = useState(null);

  useEffect(() => {
    // Quando chega via o portal (AdminMembrosPortal.jsx / AdminCriancasPortal.jsx),
    // o papel de quem esta logado ja foi checado uma vez la -- evita bater
    // /auth/me de novo aqui e reaparecer um "Carregando..." por cima do que
    // o portal ja resolveu.
    if (portalAccess !== undefined) {
      setStatus(portalAccess ? "ready" : "forbidden");
      return undefined;
    }
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const effectiveMeRoles = getEffectiveRoles(me);
        const canAccess = effectiveMeRoles.includes("ADMIN") || effectiveMeRoles.includes("SECRETARIA");
        if (canAccess && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, [portalAccess]);

  const loadMembers = useCallback(async (searchTerm) => {
    setMembersLoading(true);
    setFeedback("");
    try {
      const qs = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const data = await apiFetch(`/members${qs}`, { method: "GET" });
      setMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback("Não foi possível carregar a lista de membros agora.");
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadMembers("");
  }, [status, loadMembers]);

  const loadLinkableUsers = useCallback(async () => {
    try {
      const data = await apiFetch("/members/linkable-users", { method: "GET" });
      setLinkableUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setLinkableUsers([]);
    }
  }, []);

  const loadAuditLog = useCallback(async (memberId) => {
    setAuditLogLoading(true);
    try {
      const data = await apiFetch(`/members/${encodeURIComponent(memberId)}/audit-log`, { method: "GET" });
      setAuditLog(Array.isArray(data) ? data : []);
    } catch (error) {
      setAuditLog([]);
    } finally {
      setAuditLogLoading(false);
    }
  }, []);

  const openCreateForm = () => {
    setEditingMember(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setAuditLog([]);
    setAuditLogOpen(false);
    loadLinkableUsers();
    setFormOpen(true);
  };

  const openEditForm = useCallback((member) => {
    setEditingMember(member);
    setAuditLog([]);
    setAuditLogOpen(false);
    loadAuditLog(member.id);
    setForm({
      fullName: member.fullName || "",
      nickname: member.nickname || "",
      email: member.email || "",
      birthDate: dateForInput(member.birthDate),
      cpf: member.cpf || "",
      address: member.address || "",
      addressComplement: member.addressComplement || "",
      state: member.state || "",
      city: member.city || "",
      neighborhood: member.neighborhood || "",
      zipCode: member.zipCode || "",
      mobilePhone: member.mobilePhone || "",
      homePhone: member.homePhone || "",
      emergencyPhone: member.emergencyPhone || "",
      emergencyContactName: member.emergencyContactName || "",
      spouseName: member.spouseName || "",
      fatherName: member.fatherName || "",
      motherName: member.motherName || "",
      specialNeeds: member.specialNeeds || "",
      baptismDate: dateForInput(member.baptismDate),
      notBaptized: Boolean(member.notBaptized),
      baptismChurch: member.baptismChurch || "",
      churchRole: member.churchRole || "",
      howMetChurch: member.howMetChurch || "",
      faithOriginProfile: member.faithOriginProfile || "",
      gender: member.gender || "",
      attendingSince: dateForInput(member.attendingSince),
      status: member.status || "ATIVO",
      notes: member.notes || "",
      linkedUserId: member.linkedUserId || ""
    });
    setFormError("");
    loadLinkableUsers();
    setFormOpen(true);
  }, [loadAuditLog, loadLinkableUsers]);

  // Deep-link vindo da aba "Partilhar" de uma trilha (mesa): admin clicou
  // em "Ver cadastro" no comentario de alguem e caiu aqui com
  // ?personId=<Member.id> -- abre o cadastro direto, sem precisar buscar
  // o nome de novo. So' dispara uma vez por carregamento da tela (ref),
  // mesmo se a pessoa editar/fechar o formulario depois.
  const [searchParams] = useSearchParams();
  const appliedPersonDeepLinkRef = useRef(false);
  useEffect(() => {
    if (appliedPersonDeepLinkRef.current) return;
    const targetId = searchParams.get("personId");
    if (!targetId || !members.length) return;
    const match = members.find((member) => member.id === targetId);
    if (match) {
      appliedPersonDeepLinkRef.current = true;
      openEditForm(match);
    }
  }, [members, searchParams, openEditForm]);

  const loadChildrenList = useCallback(async () => {
    try {
      const data = await apiFetch("/children", { method: "GET" });
      setChildrenList(Array.isArray(data) ? data : []);
    } catch (error) {
      setChildrenList([]);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadChildrenList();
  }, [status, loadChildrenList]);

  // Marca a sugestao como "Ignorar" de vez (POST .../household-
  // suggestions/ignore, ver members.js) -- antes so escondia localmente
  // (dismissedGroups) e voltava a aparecer no proximo reload.
  const ignoreHouseholdSuggestion = async (index, fingerprint) => {
    setDismissedGroups((prev) => new Set(prev).add(index));
    if (!fingerprint) return;
    try {
      await apiPost("/members/household-suggestions/ignore", { body: { fingerprint } });
    } catch (error) {
      setFeedback(error?.message || "Não foi possível salvar essa sugestão como ignorada.");
    }
  };

    const loadHouseholdSuggestions = useCallback(async () => {
    try {
      const data = await apiFetch("/members/household-suggestions", { method: "GET" });
      const list = Array.isArray(data) ? data : [];
      setHouseholdSuggestions(list);
      setHouseholdChecked(
        Object.fromEntries(
          list.map((group, index) => [index, new Set(group.members.map((m) => `${m.type || "member"}:${m.id}`))])
        )
      );
    } catch (error) {
      setHouseholdSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadHouseholdSuggestions();
  }, [status, loadHouseholdSuggestions]);

  const loadAccountLinkSuggestions = useCallback(async () => {
    try {
      const data = await apiFetch("/members/account-link-suggestions", { method: "GET" });
      setAccountLinkSuggestions(Array.isArray(data) ? data : []);
    } catch (error) {
      setAccountLinkSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadAccountLinkSuggestions();
  }, [status, loadAccountLinkSuggestions]);

  // Marca/desmarca "Músico" pra esse membro aparecer na escala -- fica na
  // Pessoa (Person.isMusician), nao no login/conta (ver
  // EscalaSlot.musicianPersonId no backend): funciona mesmo pra quem nunca
  // criou conta no site (pedido do Rodrigo, 17/10/2026).
  const handleToggleMusico = async () => {
    if (!editingMember) return;
    setMusicoSaving(true);
    try {
      const updated = await apiPatch(`/members/${encodeURIComponent(editingMember.id)}/musico`, {
        body: { isMusician: !editingMember.person?.isMusician }
      });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingMember(updated);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível atualizar o papel de músico.");
    } finally {
      setMusicoSaving(false);
    }
  };

  // Mesma ideia do handleToggleMusico acima, so' que pros 3 ministerios
  // novos (Zeladoria/Kids/Teens, pedido do Rodrigo/Carla 28/10/2026) --
  // um endpoint so' (PATCH .../ministerios) que aceita qualquer combinacao
  // das flags, entao um unico handler generico serve pros 3 checkboxes.
  const handleToggleMinisterio = async (field) => {
    if (!editingMember) return;
    setMinisteriosSaving(true);
    try {
      const updated = await apiPatch(`/members/${encodeURIComponent(editingMember.id)}/ministerios`, {
        body: { [field]: !editingMember.person?.[field] }
      });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingMember(updated);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível atualizar o ministério.");
    } finally {
      setMinisteriosSaving(false);
    }
  };

  // Confirma uma sugestao de vinculo: PATCH no membro com o linkedUserId
  // sugerido. Se o estado mudou desde que a sugestao foi calculada (ex.:
  // alguem ja vinculou essa conta por outro caminho), o backend recusa com
  // 409 -- so recarrega as sugestoes nesse caso, sem quebrar a tela.
  const linkAccountSuggestion = async (suggestion) => {
    setFeedback("");
    setLinkingMemberId(suggestion.member.id);
    try {
      const updated = await apiPatch(`/members/${encodeURIComponent(suggestion.member.id)}`, {
        body: { linkedUserId: suggestion.user.id }
      });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setAccountLinkSuggestions((prev) =>
        prev.filter((s) => s.member.id !== suggestion.member.id && s.user.id !== suggestion.user.id)
      );
    } catch (error) {
      setFeedback(error?.message || "Não foi possível vincular essa conta agora.");
      await loadAccountLinkSuggestions();
    } finally {
      setLinkingMemberId(null);
    }
  };

  const toggleHouseholdMember = (groupIndex, memberId) => {
    setHouseholdChecked((prev) => {
      const next = new Set(prev[groupIndex]);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return { ...prev, [groupIndex]: next };
    });
  };

  const removeFromHousehold = async (person) => {
    setFeedback("");
    try {
      if (person.type === "child") {
        const updated = await apiPatch(`/children/${encodeURIComponent(person.id)}`, { body: { householdId: null } });
        setChildrenList((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const updated = await apiPatch(`/members/${encodeURIComponent(person.id)}`, { body: { householdId: null } });
        setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      }
    } catch (error) {
      setFeedback(error?.message || "Não foi possível remover essa pessoa da família agora.");
    }
  };

  // Extrai memberIds/childIds de uma lista de chaves "member:<id>"/
  // "child:<id>" -- mesmo formato usado no union-find do backend, pra nao
  // confundir um Member e um Child com o mesmo id.
  function splitPersonKeys(keys) {
    const memberIds = [];
    const childIds = [];
    for (const key of keys) {
      if (key.startsWith("child:")) childIds.push(key.slice("child:".length));
      else if (key.startsWith("member:")) memberIds.push(key.slice("member:".length));
    }
    return { memberIds, childIds };
  }

  const familyViewPeople = familyViewHouseholdId
    ? [
        ...members.filter((item) => item.householdId === familyViewHouseholdId).map((item) => ({ ...item, type: "member" })),
        ...childrenList.filter((item) => item.householdId === familyViewHouseholdId).map((item) => ({ ...item, type: "child" }))
      ]
    : [];

  // Zera a busca de "adicionar" toda vez que o modal muda de familia (ou fecha),
  // pra nao vazar resultado de uma familia pra outra.
  useEffect(() => {
    setAddMemberQuery("");
    setAddMemberResults([]);
  }, [familyViewHouseholdId]);

  // Busca direto na API (nao usa `members`/`childrenList` da lista principal,
  // que podem estar filtrados pela busca de cima) -- assim da pra achar
  // qualquer membro OU crianca cadastrada pra adicionar nessa familia, mesmo
  // que nao esteja na pagina atual. Busca nos dois cadastros ao mesmo tempo.
  useEffect(() => {
    if (!familyViewHouseholdId) return undefined;
    const term = addMemberQuery.trim();
    if (!term) {
      setAddMemberResults([]);
      setAddMemberSearching(false);
      return undefined;
    }
    setAddMemberSearching(true);
    const timer = setTimeout(async () => {
      try {
        const [memberData, childData] = await Promise.all([
          apiFetch(`/members?search=${encodeURIComponent(term)}`, { method: "GET" }),
          apiFetch(`/children?search=${encodeURIComponent(term)}`, { method: "GET" })
        ]);
        const memberResults = (Array.isArray(memberData) ? memberData : []).map((item) => ({ ...item, type: "member" }));
        const childResults = (Array.isArray(childData) ? childData : []).map((item) => ({ ...item, type: "child" }));
        setAddMemberResults([...memberResults, ...childResults]);
      } catch (error) {
        setAddMemberResults([]);
      } finally {
        setAddMemberSearching(false);
      }
    }, 320);
    return () => clearTimeout(timer);
  }, [addMemberQuery, familyViewHouseholdId]);

  const addMemberCandidates = addMemberResults.filter((item) => item.householdId !== familyViewHouseholdId);

  const addMemberToHousehold = async (candidate) => {
    if (!familyViewHouseholdId || familyViewPeople.length === 0) return;
    setAddingMemberId(candidate.id);
    setFeedback("");
    try {
      // Reaproveita o mesmo endpoint da confirmacao de sugestao: passando o id
      // de alguem que ja esta na familia + o novo, o backend reusa o
      // householdId existente em vez de criar um grupo novo.
      const anchor = familyViewPeople[0];
      const { memberIds, childIds } = splitPersonKeys([`${anchor.type}:${anchor.id}`, `${candidate.type}:${candidate.id}`]);
      const result = await apiPost("/members/household/confirm", { body: { memberIds, childIds } });
      const updatedMembersById = new Map((result.members || []).map((item) => [item.id, item]));
      const updatedChildrenById = new Map((result.children || []).map((item) => [item.id, item]));
      setMembers((prev) => {
        const next = prev.map((item) => (updatedMembersById.has(item.id) ? updatedMembersById.get(item.id) : item));
        if (candidate.type === "member" && !next.some((item) => item.id === candidate.id) && updatedMembersById.has(candidate.id)) {
          next.push(updatedMembersById.get(candidate.id));
        }
        return next;
      });
      setChildrenList((prev) => {
        const next = prev.map((item) => (updatedChildrenById.has(item.id) ? updatedChildrenById.get(item.id) : item));
        if (candidate.type === "child" && !next.some((item) => item.id === candidate.id) && updatedChildrenById.has(candidate.id)) {
          next.push(updatedChildrenById.get(candidate.id));
        }
        return next;
      });
      setAddMemberQuery("");
      setAddMemberResults([]);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível adicionar essa pessoa à família agora.");
    } finally {
      setAddingMemberId(null);
    }
  };

  const confirmHousehold = async (groupIndex) => {
    const selected = Array.from(householdChecked[groupIndex] || []);
    if (selected.length < 2) {
      setFeedback("Selecione ao menos 2 pessoas para confirmar como família.");
      return;
    }
    setConfirmingGroup(groupIndex);
    setFeedback("");
    try {
      const { memberIds, childIds } = splitPersonKeys(selected);
      await apiPost("/members/household/confirm", { body: { memberIds, childIds } });
      setDismissedGroups((prev) => new Set(prev).add(groupIndex));
      await Promise.all([loadMembers(search.trim()), loadChildrenList(), loadHouseholdSuggestions()]);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível confirmar essa família agora.");
    } finally {
      setConfirmingGroup(null);
    }
  };

  // Busca no servidor com um pequeno debounce -- evita bater na API a cada tecla.
  useEffect(() => {
    if (status !== "ready") return undefined;
    const timer = setTimeout(() => loadMembers(search.trim()), 320);
    return () => clearTimeout(timer);
  }, [search, status, loadMembers]);

  const closeForm = () => {
    if (formSaving) return;
    setFormOpen(false);
    setEditingMember(null);
    setFormError("");
    setMemberNoteForm({ title: "", note: "" });
    setAuditLog([]);
    setAuditLogOpen(false);
  };

  // Mesma ideia de addVisitorInteraction (AdminVisitantes.jsx), simplificada:
  // membro so tem anotacao manual aqui (o que o Barna registra sozinho, tipo
  // WHATSAPP, chega sem passar por essa tela -- ver agent/ficha.js).
  const addMemberNote = async () => {
    if (!editingMember || !memberNoteForm.note.trim()) return;
    setMemberNoteSaving(true);
    try {
      const updated = await apiPost(`/members/${encodeURIComponent(editingMember.id)}/interactions`, {
        body: { type: "NOTE", title: memberNoteForm.title.trim() || "Anotação", note: memberNoteForm.note.trim() }
      });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingMember(updated);
      setMemberNoteForm({ title: "", note: "" });
    } catch (error) {
      setFormError("Não foi possível registrar a anotação.");
    } finally {
      setMemberNoteSaving(false);
    }
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const saveForm = async () => {
    if (!form.fullName.trim()) {
      setFormError("Nome completo é obrigatório.");
      return;
    }
    // So exige essa escolha no CADASTRO (POST) de um membro novo -- mesma
    // regra que o backend ja aplica (memberCreateSchema, so' no POST; o
    // PATCH de edicao aceita parcial de proposito, ver comentario em
    // mosaico-api/src/routes/members.js). Bloquear isso tambem na edicao
    // travava qualquer PATCH num membro antigo que nunca teve a data
    // registrada -- inclusive so' pra vincular a conta do app (reportado
    // pelo Rodrigo tentando vincular o Caio Bianchi, que nao tem a data
    // de batismo no cadastro mas obviamente ja' e' batizado -- marcar
    // "Nao e' batizado" seria simplesmente falso).
    if (!editingMember && !form.notBaptized && !form.baptismDate) {
      setFormError('Informe a data de batismo ou marque "Não é batizado".');
      return;
    }
    setFormSaving(true);
    setFormError("");
    try {
      const body = formToBody(form);
      if (editingMember) {
        const updated = await apiPatch(`/members/${encodeURIComponent(editingMember.id)}`, { body });
        setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await apiPost("/members", { body });
        setMembers((prev) => [...prev, created].sort((a, b) => a.fullName.localeCompare(b.fullName)));
      }
      setFormOpen(false);
      setEditingMember(null);
    } catch (error) {
      setFormError(error?.message || "Não foi possível salvar esse membro.");
    } finally {
      setFormSaving(false);
    }
  };

  // So inativa (marca status: INATIVO) -- nao apaga o cadastro de verdade.
  // Trocado de DELETE pra PATCH por pedido do Rodrigo: a equipe quer manter
  // historico de quem passou pelo cadastro de membresia, mesmo quando sai
  // ou foi cadastrado por engano, em vez de perder o registro de vez.
  const confirmDelete = async () => {
    const member = deleteTarget;
    if (!member) return;
    setDeletingId(member.id);
    setFeedback("");
    try {
      const updated = await apiPatch(`/members/${encodeURIComponent(member.id)}`, { body: { status: "INATIVO" } });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setDeleteTarget(null);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível inativar esse membro.");
      setDeleteTarget(null);
    } finally {
      setDeletingId(null);
    }
  };

  // Aprovar direto na lista -- so troca o status pra ATIVO, sem precisar
  // abrir o form de editar so pra isso. Continua dando pra abrir o editar
  // se a secretaria quiser revisar/corrigir dados antes de aprovar.
  const approveMember = async (member) => {
    setApprovingId(member.id);
    setFeedback("");
    try {
      const updated = await apiPatch(`/members/${encodeURIComponent(member.id)}`, { body: { status: "ATIVO" } });
      setMembers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      setFeedback(error?.message || "Não foi possível atualizar esse cadastro.");
    } finally {
      setApprovingId(null);
    }
  };

  // Abas: Ativos (so ATIVO), Pendentes (aguardando aprovacao) e Inativos
  // (INATIVO, TRANSFERIDO, FALECIDO). Quem e inativado sai da aba Ativos e
  // cai direto em Inativos, porque a aba e so um filtro do status.
  const tabOf = (member) => {
    const memberStatus = member.status || "ATIVO";
    if (memberStatus === "ATIVO") return "ativos";
    if (memberStatus === "PENDENTE") return "pendentes";
    return "inativos";
  };
  const activeMembersCount = members.filter((member) => tabOf(member) === "ativos").length;
  const inactiveMembersCount = members.filter((member) => tabOf(member) === "inativos").length;
  const pendingMembersCount = members.filter((member) => tabOf(member) === "pendentes").length;
  // Contagem por perfil de chegada na fe/igreja, so' sobre membros ATIVOS
  // (pendente/inativo nao representam bem o perfil da igreja hoje) -- pedido
  // do Rodrigo, 24/10/2026.
  const faithOriginCounts = members
    .filter((member) => tabOf(member) === "ativos")
    .reduce((acc, member) => {
      const key = member.faithOriginProfile || "NAO_INFORMADO";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

  // KPIs do dashboard de membresia (aba "Dashboard" abaixo) -- todos sobre
  // membros ATIVOS, exceto a distribuicao por status (essa precisa olhar a
  // base toda). Pedido do Rodrigo, 24/10/2026.
  const activeMembers = members.filter((member) => tabOf(member) === "ativos");

  const genderCounts = activeMembers.reduce((acc, member) => {
    const key = member.gender || "NAO_INFORMADO";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Drill-down do card "Sexo" do dashboard -- clicar em "Nao informado"
  // expande a lista de quem esta sem o campo preenchido, com atalho pra
  // editar na hora. Pedido do Drigo, 28/10/2026.
  const membersSemSexo = activeMembers.filter((member) => !member.gender);

  const ageCounts = activeMembers.reduce((acc, member) => {
    const age = ageFromBirthDate(member.birthDate);
    if (age === null) {
      acc.NAO_INFORMADO = (acc.NAO_INFORMADO || 0) + 1;
      return acc;
    }
    const bracket = AGE_BRACKETS.find((item) => age >= item.min && age <= item.max);
    const key = bracket ? bracket.key : "NAO_INFORMADO";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const cityCounts = activeMembers.reduce((acc, member) => {
    const key = titleCaseCity(member.city) || "NAO_INFORMADO";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const sortedCityEntries = Object.entries(cityCounts)
    .filter(([key]) => key !== "NAO_INFORMADO")
    .sort((a, b) => b[1] - a[1]);
  const topCities = sortedCityEntries.slice(0, 6);
  const otherCitiesTotal = sortedCityEntries.slice(6).reduce((sum, [, count]) => sum + count, 0);
  const cityNaoInformadoCount = cityCounts.NAO_INFORMADO || 0;

  const baptismCounts = activeMembers.reduce((acc, member) => {
    let key;
    if (member.baptismDate) key = "BATIZADO";
    else if (member.notBaptized) key = "NAO_BATIZADO";
    else key = "NAO_INFORMADO";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const linkedToAppCount = activeMembers.filter((member) => member.linkedUserId).length;
  const notLinkedToAppCount = activeMembers.length - linkedToAppCount;

  const statusCounts = members.reduce((acc, member) => {
    const key = member.status || "ATIVO";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const statusFilteredMembers = members.filter((member) => tabOf(member) === tab);
  const visibleMembers = statusFilteredMembers;

  const copyShareLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2500);
    });
  };
  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à secretaria e administradores. Entre com uma conta com esse papel para ver e gerenciar o cadastro de membros.</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Gestão de membros</p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Cadastro de membresia</h1>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => loadMembers(search.trim())}
              disabled={membersLoading}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: membersLoading ? "default" : "pointer", opacity: membersLoading ? 0.7 : 1 }}
            >
              <RefreshCw size={13} />
              {membersLoading ? "Atualizando..." : "Atualizar"}
            </button>
            <button
              onClick={() => copyShareLink("/cadastro/membro")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
              title="Copia o link do formulário público de cadastro de membro"
            >
              <Share2 size={13} />
              {shareLinkCopied ? "Link copiado!" : "Compartilhar link"}
            </button>
            <button
              onClick={openCreateForm}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.16)", color: "#86EFAC", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
            >
              <Plus size={14} />
              Novo membro
            </button>
          </div>
        </div>
        <p style={{ margin: "0 0 14px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
          Cadastro próprio de membresia, separado das contas do app -- toda pessoa aqui pode, opcionalmente, estar vinculada a uma conta de login existente. Use "Compartilhar link" pra mandar o formulário público pra quem ainda não se cadastrou -- o cadastro entra como "Pendente" até alguém da secretaria confirmar.
        </p>

        <div role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
          {[
            { key: "lista", label: "Lista de membros" },
            { key: "dashboard", label: "Dashboard" }
          ].map((item) => {
            const selected = view === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setView(item.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 36,
                  padding: "0 16px",
                  borderRadius: 999,
                  border: selected ? "1px solid rgba(125,211,252,0.5)" : "1px solid rgba(148,163,184,0.28)",
                  background: selected ? "rgba(14,165,233,0.16)" : "rgba(255,255,255,0.03)",
                  color: selected ? "#7DD3FC" : "#9fb0cc",
                  fontSize: 13,
                  fontWeight: selected ? 900 : 700,
                  cursor: "pointer"
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {view === "lista" && (
          <>
        {pendingMembersCount > 0 && (
          <button
            type="button"
            onClick={() => setTab("pendentes")}
            style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 16, padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(251,191,36,0.35)", background: "rgba(251,191,36,0.1)", color: "#FDE68A", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
          >
            {pendingMembersCount} {pendingMembersCount === 1 ? "cadastro pendente" : "cadastros pendentes"} de aprovação -- clique pra ver
          </button>
        )}

        <div role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          {[
            { key: "ativos", label: "Ativos", count: activeMembersCount },
            ...(pendingMembersCount > 0 || tab === "pendentes" ? [{ key: "pendentes", label: "Pendentes", count: pendingMembersCount }] : []),
            { key: "inativos", label: "Inativos", count: inactiveMembersCount }
          ].map((item) => {
            const selected = tab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setTab(item.key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  minHeight: 40,
                  padding: "0 16px",
                  marginBottom: -1,
                  border: "none",
                  borderBottom: selected ? "2px solid #7DD3FC" : "2px solid transparent",
                  background: "transparent",
                  color: selected ? "#FFF" : "#9fb0cc",
                  fontSize: 13.5,
                  fontWeight: selected ? 900 : 700,
                  cursor: "pointer"
                }}
              >
                {item.label}
                <span style={{ minWidth: 22, padding: "2px 7px", borderRadius: 999, background: selected ? "rgba(125,211,252,0.18)" : "rgba(255,255,255,0.07)", color: selected ? "#7DD3FC" : "#9fb0cc", fontSize: 11, fontWeight: 900, textAlign: "center" }}>
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ position: "relative", maxWidth: 320, flex: "1 1 260px" }}>
            <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7d99", pointerEvents: "none" }} />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, e-mail ou cidade"
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>
        </div>

        {householdSuggestions.some((_, index) => !dismissedGroups.has(index)) && (
          <div style={{ ...cardStyle, marginBottom: 16, borderColor: "rgba(240,168,78,0.35)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 900, color: "#f0a84e" }}>
              Sugestões de família
            </p>
            <p style={{ margin: "0 0 14px", color: "#9fb0cc", fontSize: 12.5, lineHeight: 1.5 }}>
              Essas pessoas parecem ser da mesma família (endereço em comum, pai/mãe cadastrado como membro, irmãos ou cônjuge). Confira e confirme.
            </p>
            <div style={{ display: "grid", gap: 12 }}>
              {householdSuggestions.map((group, index) => {
                if (dismissedGroups.has(index)) return null;
                const checked = householdChecked[index] || new Set();
                return (
                  <div key={index} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                    <p style={{ margin: "0 0 4px", color: "#E5E7EB", fontSize: 13, fontWeight: 800 }}>{group.addressLabel || "Possível família"}</p>
                    {group.reasons && group.reasons.length > 0 && (
                      <p style={{ margin: "0 0 8px", color: "#f0a84e", fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                        {group.reasons.join(" · ")}
                      </p>
                    )}
                    <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                      {group.members.map((member) => {
                        const personKey = `${member.type || "member"}:${member.id}`;
                        return (
                          <label key={personKey} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#c7ced9", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={checked.has(personKey)}
                              onChange={() => toggleHouseholdMember(index, personKey)}
                            />
                            {member.type === "child" && (
                              <span title="Criança/adolescente" style={{ display: "inline-flex", color: "#f0a84e" }}>
                                <Baby size={13} />
                              </span>
                            )}
                            {member.fullName}
                            {member.householdId && (
                              <span style={{ fontSize: 11, color: "#7DD3FC" }}>(já em uma família)</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => confirmHousehold(index)}
                        disabled={confirmingGroup === index}
                        style={{ ...primaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12, opacity: confirmingGroup === index ? 0.7 : 1 }}
                      >
                        {confirmingGroup === index ? "Confirmando..." : "Confirmar como uma família"}
                      </button>
                      <button
                        type="button"
                        onClick={() => ignoreHouseholdSuggestion(index, group.fingerprint)}
                        style={{ ...secondaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12 }}
                      >
                        Ignorar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {accountLinkSuggestions.some((_, index) => !dismissedAccountSuggestions.has(index)) && (
          <div style={{ ...cardStyle, marginBottom: 16, borderColor: "rgba(125,211,252,0.35)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 900, color: "#7DD3FC" }}>
              Sugestões de vínculo de conta
            </p>
            <p style={{ margin: "0 0 14px", color: "#9fb0cc", fontSize: 12.5, lineHeight: 1.5 }}>
              Essas contas do app parecem ser de um membro que ainda não tem conta vinculada (mesmo e-mail ou mesmo nome). Confira e confirme.
            </p>
            <div style={{ display: "grid", gap: 12 }}>
              {accountLinkSuggestions.map((suggestion, index) => {
                if (dismissedAccountSuggestions.has(index)) return null;
                return (
                  <div key={`${suggestion.member.id}:${suggestion.user.id}`} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                    <p style={{ margin: "0 0 4px", color: "#E5E7EB", fontSize: 13, fontWeight: 800 }}>
                      {suggestion.member.fullName} <span style={{ color: "#9fb0cc", fontWeight: 600 }}>→</span> {suggestion.user.name} <span style={{ color: "#9fb0cc", fontWeight: 600 }}>({suggestion.user.email})</span>
                    </p>
                    <p style={{ margin: "0 0 10px", color: "#7DD3FC", fontSize: 11, fontWeight: 700 }}>{suggestion.reason}</p>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => linkAccountSuggestion(suggestion)}
                        disabled={linkingMemberId === suggestion.member.id}
                        style={{ ...primaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12, opacity: linkingMemberId === suggestion.member.id ? 0.7 : 1 }}
                      >
                        {linkingMemberId === suggestion.member.id ? "Vinculando..." : "Vincular"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedAccountSuggestions((prev) => new Set(prev).add(index))}
                        style={{ ...secondaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12 }}
                      >
                        Ignorar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {feedback && (
          <p style={{ margin: "0 0 16px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
            {feedback}
          </p>
        )}

        <div style={cardStyle}>
          {visibleMembers.length === 0 && !membersLoading && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              {search.trim()
                ? `Nenhum membro encontrado para "${search}".`
                : tab === "inativos"
                ? "Nenhum membro inativo."
                : tab === "pendentes"
                ? "Nenhum cadastro aguardando aprovação."
                : "Nenhum membro ativo cadastrado ainda."}
            </p>
          )}

          <div style={{ display: "grid", gap: 4 }}>
            {visibleMembers.map((member) => {
              const statusStyle = STATUS_COLORS[member.status] || STATUS_COLORS.ATIVO;
              return (
                <div
                  key={member.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto auto",
                    gap: 14,
                    alignItems: "center",
                    padding: "14px 0",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    opacity: deletingId === member.id ? 0.6 : 1
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, color: "#E5E7EB", fontSize: 14, fontWeight: 850 }}>
                      {member.fullName}
                      {member.nickname && (
                        <span style={{ color: "#9fb0cc", fontWeight: 600 }}> &middot; {member.nickname}</span>
                      )}
                    </p>
                    <p style={{ margin: "3px 0 0", color: "#9fb0cc", fontSize: 12, overflowWrap: "anywhere" }}>
                      {[
                        member.email,
                        member.mobilePhone,
                        titleCaseCity(member.city)
                      ].filter(Boolean).join(" · ") || "Sem contato cadastrado"}
                    </p>
                    {member.linkedUser && (
                      // display:flex (bloco) e nao inline-flex -- assim essa linha nunca
                      // fica colada na linha de familia logo abaixo (as duas sao <p> e,
                      // como inline-flex vira elemento inline, ficavam lado a lado sem
                      // quebra quando ambas apareciam pro mesmo membro).
                      <p style={{ margin: "4px 0 0", color: "#7DD3FC", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                        <Users size={11} /> Conta vinculada: {member.linkedUser.name}
                      </p>
                    )}
                    {member.convertedVisitorId && (
                      <p style={{ margin: "4px 0 0", color: "#FCD34D", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                        <UserMinus size={11} /> Virou visitante ·{" "}
                        <Link to="/admin/visitantes" style={{ color: "#FCD34D" }}>ver em Visitantes</Link>
                      </p>
                    )}
                    {member.householdId && (
                      // So um botao aqui -- "remover" ficava redundante do lado de fora
                      // porque o modal "ver familia" ja deixa remover cada pessoa (e
                      // agora tambem adicionar), entao nao precisa duplicar a acao na
                      // linha da lista.
                      <p style={{ margin: "4px 0 0", fontSize: 11 }}>
                        <button
                          type="button"
                          onClick={() => setFamilyViewHouseholdId(member.householdId)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            border: "1px solid rgba(240,168,78,0.35)",
                            background: "rgba(240,168,78,0.1)",
                            borderRadius: 999,
                            padding: "3px 9px 3px 8px",
                            color: "#f0a84e",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            minHeight: 24
                          }}
                        >
                          <Users size={11} /> Ver família
                        </button>
                      </p>
                    )}
                  </div>
                  {member.status === "ATIVO" ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#9fb0cc", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ADE80", flex: "none" }} />
                      Ativo
                    </span>
                  ) : (
                    <span
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${statusStyle.border}`,
                        background: statusStyle.background,
                        color: statusStyle.color,
                        fontSize: 11,
                        fontWeight: 900,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {STATUS_LABELS[member.status] || member.status}
                    </span>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                    {member.status === "PENDENTE" && !member.convertedVisitorId && (
                      <button
                        type="button"
                        onClick={() => approveMember(member)}
                        disabled={approvingId === member.id}
                        title="Aprovar cadastro"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          height: 32,
                          padding: "0 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(74,222,128,0.45)",
                          background: "rgba(34,197,94,0.16)",
                          color: "#86EFAC",
                          fontSize: 12,
                          fontWeight: 900,
                          cursor: "pointer",
                          opacity: approvingId === member.id ? 0.6 : 1,
                          whiteSpace: "nowrap"
                        }}
                      >
                        <Check size={14} /> {approvingId === member.id ? "Aprovando..." : "Aprovar"}
                      </button>
                    )}
                    <button type="button" onClick={() => openEditForm(member)} title="Editar membro" style={iconButtonStyle}>
                      <Pencil size={15} />
                    </button>
                    {tab === "inativos" && (
                      <button
                        type="button"
                        onClick={() => approveMember(member)}
                        disabled={approvingId === member.id}
                        title="Reativar membro"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          height: 32,
                          padding: "0 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(74,222,128,0.45)",
                          background: "rgba(34,197,94,0.16)",
                          color: "#86EFAC",
                          fontSize: 12,
                          fontWeight: 900,
                          cursor: "pointer",
                          opacity: approvingId === member.id ? 0.6 : 1,
                          whiteSpace: "nowrap"
                        }}
                      >
                        <RotateCcw size={14} /> {approvingId === member.id ? "Reativando..." : "Reativar"}
                      </button>
                    )}
                    {tab !== "inativos" && (
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(member)}
                      disabled={deletingId === member.id}
                      title="Inativar (mantém o histórico)"
                      style={iconButtonStyle}
                      onMouseEnter={(event) => { event.currentTarget.style.color = "#FCD34D"; }}
                      onMouseLeave={(event) => { event.currentTarget.style.color = "#8a96ab"; }}
                    >
                      <Archive size={15} />
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
          </>
        )}

        {view === "dashboard" && (
          <div style={{ display: "grid", gap: 16 }}>
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              Visão geral dos membros ativos ({activeMembers.length}) -- os números abaixo não contam pendentes nem inativos, exceto o card de status no final, que mostra a base toda.
            </p>

            <BarListCard
              title="Sexo"
              total={activeMembers.length}
              rows={[...GENDER_OPTIONS, "NAO_INFORMADO"].map((key) => ({
                key,
                label: GENDER_LABELS[key],
                value: genderCounts[key] || 0
              }))}
              onRowClick={(row) => {
                if (row.key !== "NAO_INFORMADO") return;
                setGenderDrilldownOpen((prev) => !prev);
              }}
              expandedRowKey={genderDrilldownOpen ? "NAO_INFORMADO" : null}
              renderExpanded={() => (
                <div style={{ marginTop: -4, marginBottom: 12, paddingLeft: 4, display: "grid", gap: 6 }}>
                  {membersSemSexo.map((member) => (
                    <div
                      key={member.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "6px 10px",
                        borderRadius: 8,
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(148,163,184,0.14)"
                      }}
                    >
                      <span style={{ fontSize: 12.5, color: "#c7d1e4" }}>{member.fullName}</span>
                      <button
                        type="button"
                        onClick={() => openEditForm(member)}
                        title="Editar cadastro"
                        style={{ ...iconButtonStyle, width: 26, height: 26 }}
                      >
                        <Pencil size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            />

            <BarListCard
              title="Faixa de idade"
              total={activeMembers.length}
              rows={[
                ...AGE_BRACKETS.map((bracket) => ({ key: bracket.key, label: bracket.label, value: ageCounts[bracket.key] || 0 })),
                { key: "NAO_INFORMADO", label: "Não informado", value: ageCounts.NAO_INFORMADO || 0 }
              ]}
            />

            <BarListCard
              title="Localidade"
              total={activeMembers.length}
              rows={[
                ...topCities.map(([city, count]) => ({ key: city, label: city, value: count })),
                ...(otherCitiesTotal > 0 ? [{ key: "OUTRAS", label: "Outras cidades", value: otherCitiesTotal }] : []),
                { key: "NAO_INFORMADO", label: "Não informado", value: cityNaoInformadoCount }
              ]}
            />

            <BarListCard
              title="Chegada na fé/igreja"
              total={activeMembers.length}
              rows={[...FAITH_ORIGIN_OPTIONS, "NAO_INFORMADO"].map((key) => ({
                key,
                label: FAITH_ORIGIN_LABELS[key],
                value: faithOriginCounts[key] || 0
              }))}
            />

            <BarListCard
              title="Batismo"
              total={activeMembers.length}
              rows={[
                { key: "BATIZADO", label: "Batizado", value: baptismCounts.BATIZADO || 0 },
                { key: "NAO_BATIZADO", label: "Não batizado", value: baptismCounts.NAO_BATIZADO || 0 },
                { key: "NAO_INFORMADO", label: "Não informado", value: baptismCounts.NAO_INFORMADO || 0 }
              ]}
            />

            <BarListCard
              title="Vínculo com o app"
              total={activeMembers.length}
              rows={[
                { key: "VINCULADO", label: "Tem conta vinculada", value: linkedToAppCount },
                { key: "NAO_VINCULADO", label: "Sem conta vinculada", value: notLinkedToAppCount }
              ]}
            />

            <BarListCard
              title="Status da base de membros (todos)"
              total={members.length}
              rows={STATUS_OPTIONS.map((key) => ({ key, label: STATUS_LABELS[key], value: statusCounts[key] || 0 }))}
            />
          </div>
        )}
      </div>

      {formOpen && (
        <div style={modalBackdropStyle} onClick={closeForm}>
          <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeForm} style={modalCloseButtonStyle} disabled={formSaving}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
              {editingMember ? "Editar membro" : "Novo membro"}
            </p>
            <h2 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>
              {editingMember ? editingMember.fullName : "Cadastrar na membresia"}
            </h2>

            {formError && (
              <p ref={formErrorRef} style={{ margin: "10px 0 0", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
                {formError}
              </p>
            )}

            <p style={sectionTitleStyle}>Dados pessoais</p>
            <FieldRow>
              <Field label="Nome completo" required type="text" value={form.fullName} onChange={(e) => updateField("fullName", e.target.value)} />
              <Field label="Apelido" placeholder="Como a pessoa prefere ser chamada" type="text" value={form.nickname} onChange={(e) => updateField("nickname", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Data de nascimento" type="date" value={form.birthDate} onChange={(e) => updateField("birthDate", e.target.value)} />
              <Field label="CPF" type="text" value={form.cpf} onChange={(e) => updateField("cpf", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <div>
                <label style={fieldLabelStyle}>Sexo</label>
                <select style={selectStyle} value={form.gender} onChange={(e) => updateField("gender", e.target.value)}>
                  <option value="">Não informado</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option} value={option}>{GENDER_LABELS[option]}</option>
                  ))}
                </select>
              </div>
            </FieldRow>
            <FieldRow>
              <Field label="Nome do pai" type="text" value={form.fatherName} onChange={(e) => updateField("fatherName", e.target.value)} />
              <Field label="Nome da mãe" type="text" value={form.motherName} onChange={(e) => updateField("motherName", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Nome do cônjuge" type="text" value={form.spouseName} onChange={(e) => updateField("spouseName", e.target.value)} />
              <div>
                <label style={fieldLabelStyle}>Necessidade especial</label>
                <input style={inputStyle} type="text" value={form.specialNeeds} onChange={(e) => updateField("specialNeeds", e.target.value)} />
              </div>
            </FieldRow>

            <p style={sectionTitleStyle}>Contato</p>
            <FieldRow>
              <Field label="E-mail" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} />
              <Field label="Celular" type="text" value={form.mobilePhone} onChange={(e) => updateField("mobilePhone", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Telefone fixo" type="text" value={form.homePhone} onChange={(e) => updateField("homePhone", e.target.value)} />
              <Field label="Telefone de emergência" type="text" value={form.emergencyPhone} onChange={(e) => updateField("emergencyPhone", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="Nome do contato de emergência" type="text" value={form.emergencyContactName} onChange={(e) => updateField("emergencyContactName", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Endereço</p>
            <FieldRow>
              <Field label="Endereço" type="text" value={form.address} onChange={(e) => updateField("address", e.target.value)} />
              <Field label="Complemento" type="text" value={form.addressComplement} onChange={(e) => updateField("addressComplement", e.target.value)} />
            </FieldRow>
            <FieldRow columns={3}>
              <Field label="Bairro" type="text" value={form.neighborhood} onChange={(e) => updateField("neighborhood", e.target.value)} />
              <Field label="Cidade" type="text" value={form.city} onChange={(e) => updateField("city", e.target.value)} />
              <Field label="Estado" type="text" value={form.state} onChange={(e) => updateField("state", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="CEP" type="text" value={form.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Vida na igreja</p>
            <FieldRow>
              <div>
                <label style={fieldLabelStyle}>
                  Data de batismo
                  {/* So mostra como obrigatorio no cadastro novo -- na edicao
                      de um membro ja existente isso e opcional de verdade
                      agora (ver saveForm), entao o asterisco aqui seguia o
                      mesmo criterio ficaria enganoso. */}
                  {!editingMember && !form.notBaptized && <span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span>}
                </label>
                <input
                  style={{ ...inputStyle, opacity: form.notBaptized ? 0.5 : 1 }}
                  type="date"
                  value={form.baptismDate}
                  disabled={form.notBaptized}
                  onChange={(e) => updateField("baptismDate", e.target.value)}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: "#9fb0cc", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.notBaptized}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        notBaptized: checked,
                        baptismDate: checked ? "" : prev.baptismDate,
                        baptismChurch: checked ? "" : prev.baptismChurch
                      }));
                    }}
                  />
                  Não é batizado
                </label>
              </div>
              <div>
                <label style={fieldLabelStyle}>Igreja onde se batizou</label>
                <input
                  style={{ ...inputStyle, opacity: form.notBaptized ? 0.5 : 1 }}
                  type="text"
                  value={form.baptismChurch}
                  disabled={form.notBaptized}
                  onChange={(e) => updateField("baptismChurch", e.target.value)}
                />
              </div>
            </FieldRow>
            <FieldRow>
              <Field label="Como conheceu a IBM" type="text" value={form.howMetChurch} onChange={(e) => updateField("howMetChurch", e.target.value)} />
              <Field label="Começou a frequentar em" type="date" value={form.attendingSince} onChange={(e) => updateField("attendingSince", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <div>
                <label style={fieldLabelStyle}>Chegada na fé/igreja</label>
                <select style={selectStyle} value={form.faithOriginProfile} onChange={(e) => updateField("faithOriginProfile", e.target.value)}>
                  <option value="">Não informado</option>
                  {FAITH_ORIGIN_OPTIONS.map((option) => (
                    <option key={option} value={option}>{FAITH_ORIGIN_LABELS[option]}</option>
                  ))}
                </select>
              </div>
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="Função que exerce ou exercia" type="text" value={form.churchRole} onChange={(e) => updateField("churchRole", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Status e vínculo com o app</p>
            <FieldRow>
              <div>
                <label style={fieldLabelStyle}>Situação</label>
                <select style={selectStyle} value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{STATUS_LABELS[option]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={fieldLabelStyle}>Conta do app vinculada</label>
                <select style={selectStyle} value={form.linkedUserId} onChange={(e) => updateField("linkedUserId", e.target.value)}>
                  <option value="">Nenhuma</option>
                  {editingMember?.linkedUser && !linkableUsers.some((u) => u.id === editingMember.linkedUser.id) && (
                    <option value={editingMember.linkedUser.id}>{editingMember.linkedUser.name} ({editingMember.linkedUser.email})</option>
                  )}
                  {linkableUsers.map((user) => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email || "sem e-mail"})</option>
                  ))}
                </select>
              </div>
            </FieldRow>
            {editingMember && (
              <FieldRow columns={1}>
                <div>
                  <label
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: musicoSaving ? "default" : "pointer", opacity: musicoSaving ? 0.6 : 1 }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(editingMember.person?.isMusician)}
                      disabled={musicoSaving}
                      onChange={handleToggleMusico}
                    />
                    <span style={{ fontSize: 13, color: "#e5e7eb" }}>
                      Músico (aparece na escala de músicos, tenha ou não conta no site)
                    </span>
                  </label>
                </div>
              </FieldRow>
            )}
            {editingMember && (
              <FieldRow columns={1}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { field: "isZelador", texto: "Zeladoria (aparece na escala de Zeladoria)" },
                    { field: "isKidsVoluntario", texto: "Kids (aparece na escala de Kids)" },
                    { field: "isTeensVoluntario", texto: "Teens (aparece na escala de Teens)" },
                    { field: "isMidiaVoluntario", texto: "Mídia (aparece na escala de Mídia)" },
                    { field: "isCafeVoluntario", texto: "Café (aparece na escala de Café)" },
                    { field: "isRecepcaoVoluntario", texto: "Recepção (aparece na escala de Recepção)" },
                    { field: "isPodcastVoluntario", texto: "Podcast (aparece na escala de Podcast)" },
                    { field: "isPregacaoVoluntario", texto: "Pregação (aparece na escala de Pregação)" },
                    { field: "isTeensDevocionalVoluntario", texto: "Devocional Teens (aparece na escala de Devocional Teens)" }
                  ].map(({ field, texto }) => (
                    <label
                      key={field}
                      style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: ministeriosSaving ? "default" : "pointer", opacity: ministeriosSaving ? 0.6 : 1 }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(editingMember.person?.[field])}
                        disabled={ministeriosSaving}
                        onChange={() => handleToggleMinisterio(field)}
                      />
                      <span style={{ fontSize: 13, color: "#e5e7eb" }}>{texto}</span>
                    </label>
                  ))}
                </div>
              </FieldRow>
            )}
            <FieldRow columns={1}>
              <div>
                <label style={fieldLabelStyle}>Observações da secretaria</label>
                <textarea style={textareaStyle} rows={3} value={form.notes} onChange={(e) => updateField("notes", e.target.value)} />
              </div>
            </FieldRow>

            {editingMember && (
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(148,163,184,0.16)" }}>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 900, color: "#FFF" }}>Histórico</p>
                <p style={{ margin: "0 0 12px", fontSize: 12, color: "#9fb0cc", lineHeight: 1.5 }}>
                  Mensagens de WhatsApp do Barna (enviadas e recebidas) e anotações da equipe.
                </p>

                <div style={{ display: "grid", gap: 9, marginBottom: 14, maxHeight: 260, overflowY: "auto", paddingRight: 6 }}>
                  {(editingMember.interactions || []).map((interaction) => {
                    const kindStyle = memberInteractionKindStyle[interaction.type] || { color: "#94A3B8", label: interaction.type };
                    const createdAt = new Date(interaction.createdAt);
                    const createdLabel = `${createdAt.toLocaleDateString("pt-BR")} às ${createdAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
                    return (
                      <div key={interaction.id} style={{ boxSizing: "border-box", border: "1px solid rgba(148,163,184,0.16)", borderLeft: `3px solid ${kindStyle.color}`, background: "rgba(255,255,255,0.035)", padding: 10 }}>
                        <p style={{ margin: "0 0 4px", color: "#F8FAFC", fontSize: 13, fontWeight: 900, overflowWrap: "anywhere" }}>{interaction.title}</p>
                        <p style={{ margin: "0 0 5px", color: kindStyle.color, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 }}>
                          {kindStyle.label} • {createdLabel}{interaction.createdBy?.name ? ` • ${interaction.createdBy.name}` : ""}
                        </p>
                        {interaction.note && <p style={{ margin: 0, color: "#B6C5D8", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>{interaction.note}</p>}
                      </div>
                    );
                  })}
                  {(!editingMember.interactions || editingMember.interactions.length === 0) && (
                    <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Sem interações registradas ainda.</p>
                  )}
                </div>

                <input
                  value={memberNoteForm.title}
                  onChange={(e) => setMemberNoteForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Título (opcional)"
                  style={{ ...inputStyle, marginBottom: 8 }}
                />
                <textarea
                  value={memberNoteForm.note}
                  onChange={(e) => setMemberNoteForm((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="Ligação, visita ou qualquer contato com esse membro"
                  rows={3}
                  style={{ ...textareaStyle, marginBottom: 8 }}
                />
                <button
                  type="button"
                  onClick={addMemberNote}
                  disabled={memberNoteSaving || !memberNoteForm.note.trim()}
                  style={{ ...secondaryButtonStyle, opacity: memberNoteSaving || !memberNoteForm.note.trim() ? 0.6 : 1 }}
                >
                  {memberNoteSaving ? "Registrando..." : "Registrar anotação"}
                </button>
              </div>
            )}

            {editingMember && (
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(148,163,184,0.16)" }}>
                <button
                  type="button"
                  onClick={() => setAuditLogOpen((prev) => !prev)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <span>
                    <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 900, color: "#FFF", textAlign: "left" }}>Auditoria de edições</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc", lineHeight: 1.5, textAlign: "left" }}>
                      Quem alterou este cadastro e o que mudou (registrado a partir de 24/10/2026).
                    </p>
                  </span>
                  <span style={{ color: "#9fb0cc", fontSize: 18, transform: auditLogOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▸</span>
                </button>

                {auditLogOpen && (
                  <div style={{ marginTop: 12, display: "grid", gap: 8, maxHeight: 260, overflowY: "auto", paddingRight: 6 }}>
                    {auditLogLoading && <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Carregando...</p>}
                    {!auditLogLoading && auditLog.length === 0 && (
                      <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Sem edições registradas ainda.</p>
                    )}
                    {!auditLogLoading && auditLog.map((entry) => {
                      const createdAt = new Date(entry.createdAt);
                      const createdLabel = `${createdAt.toLocaleDateString("pt-BR")} às ${createdAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
                      return (
                        <div key={entry.id} style={{ boxSizing: "border-box", border: "1px solid rgba(148,163,184,0.16)", borderLeft: "3px solid #60A5FA", background: "rgba(255,255,255,0.035)", padding: 10 }}>
                          <p style={{ margin: "0 0 4px", color: "#F8FAFC", fontSize: 13, fontWeight: 900 }}>
                            {MEMBER_FIELD_LABELS[entry.field] || entry.field}
                          </p>
                          <p style={{ margin: "0 0 5px", color: "#60A5FA", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 }}>
                            {createdLabel}{entry.actor?.name ? ` • ${entry.actor.name}` : " • (ator não identificado)"}
                          </p>
                          <p style={{ margin: 0, color: "#B6C5D8", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                            {formatAuditValue(entry.field, entry.oldValue)} → {formatAuditValue(entry.field, entry.newValue)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button type="button" onClick={closeForm} disabled={formSaving} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={saveForm} disabled={formSaving} style={{ ...primaryButtonStyle, opacity: formSaving ? 0.7 : 1 }}>
                {formSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {familyViewHouseholdId && (
        <div style={modalBackdropStyle} onClick={() => setFamilyViewHouseholdId(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 420, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setFamilyViewHouseholdId(null)} style={modalCloseButtonStyle}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#f0a84e", textTransform: "uppercase", fontWeight: 900 }}>Família confirmada</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>Quem está nessa família</h2>
            <div style={{ display: "grid", gap: 4, marginBottom: 4 }}>
              {familyViewPeople.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "10px 0",
                    borderTop: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, color: "#E5E7EB", fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                      {item.type === "child" && (
                        <span title="Criança/adolescente" style={{ display: "inline-flex", color: "#f0a84e" }}>
                          <Baby size={13} />
                        </span>
                      )}
                      {item.fullName}
                    </p>
                    <p style={{ margin: "2px 0 0", color: "#9fb0cc", fontSize: 11, overflowWrap: "anywhere" }}>
                      {item.type === "child"
                        ? [item.fatherPhone || item.motherPhone || item.guardianPhone, item.city].filter(Boolean).join(" · ") || "Sem contato cadastrado"
                        : [item.email, item.mobilePhone, item.city].filter(Boolean).join(" · ") || "Sem contato cadastrado"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFromHousehold(item)}
                    style={{ border: "none", background: "none", padding: 0, color: "#9fb0cc", fontSize: 11, fontWeight: 700, textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    remover
                  </button>
                </div>
              ))}
              {familyViewPeople.length === 0 && (
                <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>Ninguém mais nessa família (a última pessoa foi removida).</p>
              )}
            </div>

            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#9fb0cc", textTransform: "uppercase", fontWeight: 900 }}>
                Adicionar alguém a essa família
              </p>
              <div style={{ position: "relative" }}>
                <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7d99", pointerEvents: "none" }} />
                <input
                  type="text"
                  value={addMemberQuery}
                  onChange={(event) => setAddMemberQuery(event.target.value)}
                  placeholder="Buscar por nome, e-mail ou cidade..."
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 10px 8px 30px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#E5E7EB",
                    fontSize: 13
                  }}
                />
              </div>
              {addMemberQuery.trim() && (
                <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
                  {addMemberSearching && (
                    <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Buscando...</p>
                  )}
                  {!addMemberSearching && addMemberCandidates.length === 0 && (
                    <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Nenhum membro ou criança encontrado(a).</p>
                  )}
                  {!addMemberSearching &&
                    addMemberCandidates.slice(0, 6).map((candidate) => (
                      <div
                        key={`${candidate.type}-${candidate.id}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.03)"
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, color: "#E5E7EB", fontSize: 12.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
                            {candidate.type === "child" && (
                              <span title="Criança/adolescente" style={{ display: "inline-flex", color: "#f0a84e" }}>
                                <Baby size={12} />
                              </span>
                            )}
                            {candidate.fullName}
                          </p>
                          {candidate.householdId && (
                            <p style={{ margin: "1px 0 0", color: "#f0a84e", fontSize: 10.5 }}>Já está em outra família confirmada -- será movido(a) para esta.</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => addMemberToHousehold(candidate)}
                          disabled={addingMemberId === candidate.id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            border: "none",
                            borderRadius: 999,
                            padding: "5px 10px",
                            background: "rgba(74,222,128,0.15)",
                            color: "#4ADE80",
                            fontSize: 11.5,
                            fontWeight: 800,
                            cursor: addingMemberId === candidate.id ? "default" : "pointer",
                            opacity: addingMemberId === candidate.id ? 0.6 : 1,
                            whiteSpace: "nowrap"
                          }}
                        >
                          <Plus size={12} /> {addingMemberId === candidate.id ? "Adicionando..." : "Adicionar"}
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" onClick={() => setFamilyViewHouseholdId(null)} style={secondaryButtonStyle}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={modalBackdropStyle} onClick={() => !deletingId && setDeleteTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 420, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setDeleteTarget(null)} style={modalCloseButtonStyle} disabled={Boolean(deletingId)}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#FCD34D", textTransform: "uppercase", fontWeight: 900 }}>Inativar membro</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{deleteTarget.fullName}</h2>
            <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              Marca esse cadastro como "Inativo". Ele some da lista de pendentes/ativos do dia a dia, mas o histórico continua guardado -- não apaga nada. Se ela tiver conta no app, a conta passa a ter só acesso de visitante (perde o acesso de membro, mas continua podendo entrar); volta a ser membro automaticamente se você reativar o cadastro.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(deletingId)}
                style={{ minHeight: 40, padding: "0 18px", borderRadius: 9, border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.14)", color: "#FDE68A", fontSize: 13, fontWeight: 900, cursor: "pointer", opacity: deletingId ? 0.7 : 1 }}
              >
                {deletingId ? "Inativando..." : "Inativar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
